import React, { useState, useEffect, useRef } from 'react';
import { Button, Table, message, Progress, Space, Checkbox, Card } from 'antd';
import firebase from '../../../firebase';

const ItemSync = ({ db }) => {
    const [syncOrgs, setSyncOrgs] = useState([]);
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncMigrating, setSyncMigrating] = useState(false);
    const [syncProgress, setSyncProgress] = useState(0);
    const [syncPaused, setSyncPaused] = useState(false);
    const [syncStopped, setSyncStopped] = useState(false);
    const [deleteAfterSync, setDeleteAfterSync] = useState(false);
    const [syncingRowId, setSyncingRowId] = useState(null);
    const syncPausedRef = useRef(false);
    const syncStoppedRef = useRef(false);

    useEffect(() => { syncPausedRef.current = syncPaused; }, [syncPaused]);
    useEffect(() => { syncStoppedRef.current = syncStopped; }, [syncStopped]);

    const waitIfPaused = async (pRef, sRef) => {
        while (pRef.current && !sRef.current) {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    };

    const fetchOrgs = async () => {
        setSyncLoading(true);
        try {
            const snapshot = await db.collection('organizations').where('buyer', '==', true).get();
            const orgsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSyncOrgs(orgsData.filter(org => org.seller));
        } catch (error) {
            message.error("Failed to fetch organizations");
        }
        setSyncLoading(false);
    };

    useEffect(() => {
        fetchOrgs();
    }, []);

    const migrateAllItemsSync = async () => {
        setSyncMigrating(true);
        setSyncStopped(false);
        setSyncPaused(false);
        syncStoppedRef.current = false;
        syncPausedRef.current = false;
        setSyncProgress(0);

        const total = syncOrgs.length;
        if (total === 0) { message.info("No sync orgs found"); setSyncMigrating(false); return; }

        let processed = 0;
        let totalItemsProcessed = 0;

        for (const org of syncOrgs) {
            if (syncStoppedRef.current) break;
            await waitIfPaused(syncPausedRef, syncStoppedRef);
            if (syncStoppedRef.current) break;

            try {
                const buyerId = org.id;
                const sellerId = org.seller;
                const subColRef = db.collection('items_sell').doc(buyerId).collection('items');

                if (deleteAfterSync) {
                    await db.collection('items_sell').doc(buyerId).delete();
                }

                let lastItemDoc = null;
                while (true) {
                    if (syncStoppedRef.current) break;
                    await waitIfPaused(syncPausedRef, syncStoppedRef);
                    if (syncStoppedRef.current) break;

                    let itemQuery = subColRef.orderBy(firebase.firestore.FieldPath.documentId()).limit(200);
                    if (lastItemDoc) itemQuery = itemQuery.startAfter(lastItemDoc);

                    const itemSnapshot = await itemQuery.get({ source: 'server' });
                    if (itemSnapshot.empty) break;

                    const batchPromises = itemSnapshot.docs.map(async (item) => {
                        const data = item.data();
                        const targetRef = db
                            .collection('items_sell').doc(sellerId)
                            .collection('buyers').doc(buyerId)
                            .collection('items').doc(item.id);

                        delete data.tokenMap;
                        await targetRef.set({ ...data, migratedAt: new Date() });

                        if (deleteAfterSync) {
                            await item.ref.delete();
                        }
                    });

                    await Promise.all(batchPromises);
                    await new Promise(resolve => setTimeout(resolve, 500));

                    totalItemsProcessed += itemSnapshot.size;
                    lastItemDoc = itemSnapshot.docs[itemSnapshot.docs.length - 1];
                    console.log(`Org ${buyerId}: ${totalItemsProcessed} items migrated...`);
                }
            } catch (error) {
                console.error(`Error syncing org ${org.id}:`, error);
                message.warning(`Error at org ${org.id}, skipping...`);
            }

            processed++;
            setSyncProgress(Math.round((processed / total) * 100));
        }

        setSyncMigrating(false);
        message[syncStoppedRef.current ? 'warning' : 'success'](
            syncStoppedRef.current ? "Stopped" : `Completed (Total items: ${totalItemsProcessed})`
        );
        fetchOrgs();
    };

    const [tsReindexing, setTsReindexing] = useState(false);
    const [tsProgress, setTsProgress] = useState(0);
    const [tsPaused, setTsPaused] = useState(false);
    const [tsStopped, setTsStopped] = useState(false);
    const [tsTotalProcessed, setTsTotalProcessed] = useState(0);
    const [tsCurrentOrg, setTsCurrentOrg] = useState("");

    const tsPausedRef = useRef(false);
    const tsStoppedRef = useRef(false);

    useEffect(() => { tsPausedRef.current = tsPaused; }, [tsPaused]);
    useEffect(() => { tsStoppedRef.current = tsStopped; }, [tsStopped]);

    const reindexToTypesense = async () => {
        setTsReindexing(true);
        setTsStopped(false);
        setTsPaused(false);
        tsStoppedRef.current = false;
        tsPausedRef.current = false;
        setTsProgress(0);
        setTsTotalProcessed(0);

        try {
            message.loading({ content: "Resetting Typesense collection...", key: "tsReset" });
            const resetFn = firebase.functions().httpsCallable('resetItemsSellTypeSense');
            await resetFn();
            message.success({ content: "Collection reset. Starting reindex...", key: "tsReset", duration: 2 });

            const reindexFn = firebase.functions().httpsCallable('addItemSellAllDataToTypeSense');
            const totalOrgs = syncOrgs.length;
            let orgCounter = 0;

            for (const org of syncOrgs) {
                if (tsStoppedRef.current) break;
                await waitIfPaused(tsPausedRef, tsStoppedRef);
                if (tsStoppedRef.current) break;

                const buyerId = org.id;
                const sellerId = org.seller;
                setTsCurrentOrg(buyerId);

                let hasMoreInOrg = true;
                let lastId = null;

                while (hasMoreInOrg) {
                    if (tsStoppedRef.current) break;
                    await waitIfPaused(tsPausedRef, tsStoppedRef);
                    if (tsStoppedRef.current) break;

                    const result = await reindexFn({ buyerId, sellerId, lastId });
                    const { data } = result;

                    if (data.success) {
                        hasMoreInOrg = data.hasMore;
                        lastId = data.nextLastId;

                        const processedInBatch = data.count || 0;
                        setTsTotalProcessed(prev => prev + processedInBatch);

                        console.log(`Typesense Sync: Org ${buyerId} | Processed ${processedInBatch} items. Total: ${tsTotalProcessed + processedInBatch}`);
                    } else {
                        throw new Error(data.message || "Unknown error during reindexing");
                    }
                }

                orgCounter++;
                setTsProgress(Math.round((orgCounter / totalOrgs) * 100));
            }

            if (!tsStoppedRef.current) {
                message.success(`Typesense reindexing completed! Total items: ${tsTotalProcessed}`);
            } else {
                message.warning("Typesense reindexing stopped.");
            }

        } catch (error) {
            console.error("Typesense reindex error:", error);
            message.error(`Reindexing failed: ${error.message}`);
        }

        setTsReindexing(false);
    };

    const syncSingleOrg = async (org) => {
        setSyncingRowId(org.id);
        const buyerId = org.id;
        const sellerId = org.seller;
        
        try {
            // 1. Firestore Migration
            message.loading({ content: `Migrating Firestore path for ${org.name || buyerId}...`, key: 'syncSingle' });
            const subColRef = db.collection('items_sell').doc(buyerId).collection('items');

            if (deleteAfterSync) {
                await db.collection('items_sell').doc(buyerId).delete();
            }

            let lastItemDoc = null;
            let migratedCount = 0;
            while (true) {
                let itemQuery = subColRef.orderBy(firebase.firestore.FieldPath.documentId()).limit(200);
                if (lastItemDoc) itemQuery = itemQuery.startAfter(lastItemDoc);

                const itemSnapshot = await itemQuery.get({ source: 'server' });
                if (itemSnapshot.empty) break;

                const batchPromises = itemSnapshot.docs.map(async (item) => {
                    const data = item.data();
                    const targetRef = db
                        .collection('items_sell').doc(sellerId)
                        .collection('buyers').doc(buyerId)
                        .collection('items').doc(item.id);

                    delete data.tokenMap;
                    await targetRef.set({ ...data, migratedAt: new Date() });

                    if (deleteAfterSync) {
                        await item.ref.delete();
                    }
                });

                await Promise.all(batchPromises);
                await new Promise(resolve => setTimeout(resolve, 500));
                migratedCount += itemSnapshot.size;
                lastItemDoc = itemSnapshot.docs[itemSnapshot.docs.length - 1];
            }

            // 2. Typesense Sync
            message.loading({ content: `Syncing to Typesense for ${org.name || buyerId}...`, key: 'syncSingle' });
            const reindexFn = firebase.functions().httpsCallable('addItemSellAllDataToTypeSense');
            
            let hasMoreInOrg = true;
            let lastId = null;
            let tsCount = 0;

            while (hasMoreInOrg) {
                const result = await reindexFn({ buyerId, sellerId, lastId });
                const { data } = result;

                if (data.success) {
                    hasMoreInOrg = data.hasMore;
                    lastId = data.nextLastId;
                    tsCount += (data.count || 0);
                } else {
                    throw new Error(data.message || "Unknown error during Typesense sync");
                }
            }

            message.success({ content: `Successfully synced ${org.name || buyerId}! (FS: ${migratedCount}, TS: ${tsCount})`, key: 'syncSingle', duration: 4 });
        } catch (error) {
            console.error(error);
            message.error({ content: `Sync failed for ${org.id}: ${error.message}`, key: 'syncSingle' });
        }
        
        setSyncingRowId(null);
        fetchOrgs();
    };

    const deleteOldDataSingleOrg = async (org) => {
        if (!window.confirm(`Are you sure you want to delete all data from the OLD path for ${org.name || org.id}?`)) return;
        
        setSyncingRowId(org.id);
        const buyerId = org.id;
        try {
            message.loading({ content: `Deleting old data for ${org.name || buyerId}...`, key: 'deleteSingle' });
            const subColRef = db.collection('items_sell').doc(buyerId).collection('items');
            
            await db.collection('items_sell').doc(buyerId).delete();

            let lastItemDoc = null;
            let deletedCount = 0;
            while (true) {
                let itemQuery = subColRef.orderBy(firebase.firestore.FieldPath.documentId()).limit(200);
                if (lastItemDoc) itemQuery = itemQuery.startAfter(lastItemDoc);

                const itemSnapshot = await itemQuery.get({ source: 'server' });
                if (itemSnapshot.empty) break;

                const batchPromises = itemSnapshot.docs.map(item => item.ref.delete());

                await Promise.all(batchPromises);
                deletedCount += itemSnapshot.size;
                lastItemDoc = itemSnapshot.docs[itemSnapshot.docs.length - 1];
                await new Promise(resolve => setTimeout(resolve, 300));
            }
            message.success({ content: `Successfully deleted ${deletedCount} legacy items for ${org.name || buyerId}`, key: 'deleteSingle' });
        } catch (error) {
            console.error(error);
            message.error({ content: `Delete failed: ${error.message}`, key: 'deleteSingle' });
        }
        setSyncingRowId(null);
        fetchOrgs();
    };

    const columnsOrgs = [
        { title: 'Org ID', dataIndex: 'id', key: 'id' },
        { title: 'Org Name', dataIndex: 'name', key: 'name' },
        { title: 'Seller ID', dataIndex: 'seller', key: 'seller' },
        {
            title: 'Multi Seller Status',
            key: 'multi_seller',
            render: (_, record) => record.resellers && record.resellers.length > 0 ? <span style={{ color: 'green' }}>Converted</span> : <span style={{ color: 'orange' }}>Pending</span>
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button 
                        size="small" 
                        type="primary" 
                        onClick={() => syncSingleOrg(record)}
                        loading={syncingRowId === record.id}
                        disabled={syncMigrating || tsReindexing || (syncingRowId && syncingRowId !== record.id)}
                    >
                        Sync
                    </Button>
                    <Button 
                        size="small" 
                        danger
                        onClick={() => deleteOldDataSingleOrg(record)}
                        disabled={syncMigrating || tsReindexing || (syncingRowId && syncingRowId !== record.id)}
                    >
                        Delete old data after sync
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {/* Firestore Migration Section */}
            <Card title="1. Firestore Path Migration" size="small">
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Relocate items from buyer path to seller/buyer path</span>
                    <Space size="large">
                        <Checkbox
                            checked={deleteAfterSync}
                            onChange={e => setDeleteAfterSync(e.target.checked)}
                            disabled={syncMigrating}
                        >
                            Delete old data after sync
                        </Checkbox>
                        <Space>
                            {syncMigrating ? (
                                <>
                                    <Button onClick={() => setSyncPaused(!syncPaused)}>{syncPaused ? "Resume" : "Pause"}</Button>
                                    <Button danger onClick={() => { setSyncStopped(true); syncStoppedRef.current = true; }}>Stop</Button>
                                </>
                            ) : (
                                <Button type="primary" onClick={migrateAllItemsSync}>Start Sync</Button>
                            )}
                        </Space>
                    </Space>
                </div>
                {syncMigrating && <Progress percent={syncProgress} status={syncPaused ? "active" : "normal"} style={{ marginBottom: '16px' }} />}
            </Card>

            {/* Typesense Sync Section */}
            <Card title="2. Typesense Full Reindex" size="small">
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontWeight: 500 }}>Sync all migrated items to Typesense</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>
                            {tsReindexing && `Current Org: ${tsCurrentOrg} | Total Processed: ${tsTotalProcessed}`}
                        </div>
                    </div>
                    <Space>
                        {tsReindexing ? (
                            <>
                                <Button onClick={() => setTsPaused(!tsPaused)}>{tsPaused ? "Resume" : "Pause"}</Button>
                                <Button danger onClick={() => { setTsStopped(true); tsStoppedRef.current = true; }}>Stop</Button>
                            </>
                        ) : (
                            <Button type="primary" onClick={reindexToTypesense} disabled={syncMigrating}>Start Typesense Reindex</Button>
                        )}
                    </Space>
                </div>
                {tsReindexing && <Progress percent={tsProgress} status={tsPaused ? "active" : "normal"} style={{ marginBottom: '16px' }} />}
            </Card>

            <Table dataSource={syncOrgs} columns={columnsOrgs} rowKey="id" loading={syncLoading} pagination={{ pageSize: 15 }} />
        </div>
    );
};

export default ItemSync;
